import {
  currentLiveClassLessonAtom,
  selectedLiveClassFeedbackAtom,
} from '@openeducation/oe-it-jotai-atoms';
import {
  useLiveClassFeedbackByUUID,
  useLiveClassLesson,
  usePersonProfile,
  useLiveClassPendingFeedback,
  useStudentProfileLite,
} from '@openeducation/oe-it-providers';
import type { FeedbackResponseDTO } from '@openeducation/oe-it-services';
import type { LiveClassPendingFeedback } from '@openeducation/oe-it-shared-types';
import { useSetAtom } from 'jotai';
import * as React from 'react';

const mergeLiveClassWithPending = (
  liveClassFeedback: FeedbackResponseDTO,
  pendingFeedback: LiveClassPendingFeedback,
  studentId: number
) => {
  const { dateOfClass, teacherFeedback, lessonName = '' } = liveClassFeedback;

  return {
    ...pendingFeedback,
    dateOfClass,
    teacherFeedback,
    lessonName: lessonName ?? '',
    studentId,
  };
};

export const useStudentPendingFeedback = (
  teacherId: number,
  studentId: number,
  classroomId: number
) => {
  const _setSelectedFeedback = useSetAtom(selectedLiveClassFeedbackAtom);
  const _setCurrentLiveClassLesson = useSetAtom(currentLiveClassLessonAtom);

  const {
    personProfile: teacherProfile,
    isLoadingPersonProfile: isLoadingTeacherProfile,
  } = usePersonProfile({
    personId: teacherId,
    service: 'monitor-profile',
  });

  const { studentProfile, isLoadingStudentProfile } = useStudentProfileLite({
    studentId,
  });

  const { pendingFeedback, isLoadingPendingFeedback } =
    useLiveClassPendingFeedback({
      teacherProfile,
      studentId,
      classroomId,
    });

  const { isLoadingLiveClassFeedbackByUUID, liveClassFeedback } =
    useLiveClassFeedbackByUUID({
      feedbackUUID: pendingFeedback?.uuid,
    });

  const { isLoadingLiveClassLessons, liveClassLessons } = useLiveClassLesson({
    isTeacher: true,
    contactId: studentProfile?.contactId,
  });

  const setCurrentLiveClassLesson = React.useCallback(
    (lessonId?: number) => {
      const lesson = liveClassLessons?.find(
        ({ id }: { id: number }) => id === lessonId
      );
      _setCurrentLiveClassLesson(lesson);
    },
    [_setCurrentLiveClassLesson, liveClassLessons]
  );

  const setSelectedFeedback = React.useCallback(
    (pending: LiveClassPendingFeedback, liveClass?: FeedbackResponseDTO) => {
      const feedback = liveClass
        ? mergeLiveClassWithPending(liveClass, pending, studentId)
        : pending;

      _setSelectedFeedback(feedback);
    },
    [_setSelectedFeedback, studentId]
  );

  return {
    liveClassLessons,
    pendingFeedback,
    liveClassFeedback,
    setCurrentLiveClassLesson,
    setSelectedFeedback,
    isLoading:
      isLoadingStudentProfile ||
      isLoadingTeacherProfile ||
      isLoadingPendingFeedback ||
      isLoadingLiveClassFeedbackByUUID ||
      isLoadingLiveClassLessons,
  };
};
